"use client";
import Link from "next/link";
import { ArrowRight, Wrench, FileCheck, BookOpen, LifeBuoy } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { techSupport as data } from "@/lib/data/contact";

const accent = "var(--color-crimson)";

export function TechSupport() {
  return (
    <Section id="technical-support" className="relative">
      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny text-[var(--color-crimson)]">{data.eyebrow}</span>
            <h2 className="mt-3">{data.title}</h2>
            <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">{data.intro}</p>
          </div>
        </Reveal>
        <div className="mt-10 grid lg:grid-cols-2 gap-5">
          <Reveal delay={100}>
            <div className="glass rounded-2xl p-6 h-full" style={{ boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 18%, transparent)` }}>
              <div className="flex items-center gap-3">
                <span className="grid place-items-center h-10 w-10 rounded-lg" style={{ background: `color-mix(in oklch, ${accent} 18%, transparent)`, boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 35%, transparent)` }}>
                  <LifeBuoy className="h-4 w-4" style={{ color: accent }} />
                </span>
                <span className="eyebrow-tiny font-bold" style={{ color: accent }}>Support Tiers</span>
              </div>
              <div className="mt-4 space-y-3">
                {data.tiers.map((t) => (
                  <div key={t.tier} className="text-sm">
                    <div className="font-semibold" style={{ color: accent }}>{t.tier}</div>
                    <div className="text-[rgb(var(--fg-muted))] mt-0.5 leading-relaxed">{t.body}</div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={150}>
            <div className="glass rounded-2xl p-6 h-full" style={{ boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 18%, transparent)` }}>
              <div className="flex items-center gap-3">
                <span className="grid place-items-center h-10 w-10 rounded-lg" style={{ background: `color-mix(in oklch, ${accent} 18%, transparent)`, boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 35%, transparent)` }}>
                  <Wrench className="h-4 w-4" style={{ color: accent }} />
                </span>
                <span className="eyebrow-tiny font-bold" style={{ color: accent }}>Common Support Topics</span>
              </div>
              <ul className="mt-4 space-y-2">
                {data.topics.map((t, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[rgb(var(--fg-muted))] leading-relaxed">
                    <span className="h-1.5 w-1.5 rounded-full mt-2 shrink-0" style={{ background: accent }} />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
        <div className="mt-5 grid sm:grid-cols-2 gap-5">
          <Reveal delay={200}>
            <div className="glass rounded-2xl p-6 h-full" style={{ boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 18%, transparent)` }}>
              <div className="flex items-center gap-3">
                <FileCheck className="h-4 w-4" style={{ color: accent }} />
                <span className="eyebrow-tiny font-bold" style={{ color: accent }}>{data.access.title}</span>
              </div>
              <p className="mt-3 text-sm text-[rgb(var(--fg-muted))] leading-relaxed">{data.access.body}</p>
            </div>
          </Reveal>
          <Reveal delay={250}>
            <div className="glass rounded-2xl p-6 h-full" style={{ boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 18%, transparent)` }}>
              <div className="flex items-center gap-3">
                <BookOpen className="h-4 w-4" style={{ color: accent }} />
                <span className="eyebrow-tiny font-bold" style={{ color: accent }}>Self-Service Resources</span>
              </div>
              <ul className="mt-3 space-y-1.5">
                {data.selfService.map((s, i) => (
                  <li key={i} className="text-sm text-[rgb(var(--fg-muted))] leading-relaxed">{s}</li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
        <Reveal delay={400}>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            {data.cta.map((c, i) => (
              <Link key={c.label} href={c.href} className={i === 0 ? "btn-primary-gradient h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm" : "btn-glass h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"}>
                {c.label} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ))}
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}
